import React, { useState } from 'react'
import styled from 'styled-components'
import { VerifiedIcon, Text, TextVariant } from '@mprolab/uikit'
import type { Connector as WagmiConnector } from 'wagmi'
import {
  BASE_CHAIN_ID_NUMBER,
  ConnectorName,
  ConnectorNameEnum,
  ETH_CHAIN_ID_NUMBER,
  initialConnectorIdLocalStorageKey,
  shortenAddress
} from '@mprolab/sdk'
import { ChevronDown, LogOut, RefreshCw, X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import type { Login, LoginHandlerArgs } from './types'
import WalletPanelTokenRows from './components/WalletPanelTokenRows'
import WalletSendReceive from './components/WalletSendReceive'
import { useWalletPanelOnchainBalances } from 'hooks/useWalletPanelOnchainBalances'

export interface ConnectVerifyPanelProps {
  handleLogin: Login
  isConnectWalletPanelOpen: boolean
  setIsConnectWalletPanelOpen: (open: boolean) => void
  logout: () => void
  isUserVerified: boolean
  onVerifyUser: () => void | Promise<void>
  account?: string
  isActive: boolean
  handlePanelBlur: () => void
  connectors: Array<{ title: string; icon: React.ReactNode; connectorId: ConnectorName }>
  chainId?: number
  setupNetwork?: (...args: any[]) => any
}

/*
 * The terms, at the bottom of the panel, every time it opens.
 *
 * Not a gate and not a checkbox. There is no session to attach a consent to
 * and no account to attach it to — wallets connect, disconnect and change, and
 * the platform deliberately stores nothing about who anybody is. So a stored
 * "accepted" flag would be a record of a person we do not have, answering a
 * question nobody could later verify.
 *
 * What is owed instead is access: whoever connects can read the terms at the
 * moment they connect, without going looking. Small, quiet and always there
 * does that; a dialog demanding a click does not do it better, it only trains
 * people to click.
 */
const TermsNote = styled.div`
  margin-top: auto;
  padding: 16px 0 4px;
  font-size: 11px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.gray6};
  flex-shrink: 0;

  a {
    color: inherit;
    text-decoration: underline;
  }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
`

const Panel = styled.div`
  position: relative;
  width: 428px;
  max-width: 100%;
  height: 100vh;
  background: #121212;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: -24px 0 60px rgba(0, 0, 0, 0.35);
  padding: 24px 24px 32px;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.white};
  /* A column so the terms note can sit at the bottom rather than trailing the
     content: with only a provider list above it, flow order would leave it
     halfway up a full-height panel. */
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100vw;
    padding: 20px 16px 28px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`

const HeaderMeta = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`

const NetworkSelectorWrap = styled.div`
  position: relative;
`

const NetworkSelector = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 170px;
  height: 48px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: #1b1b1b;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`

const NetworkMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  padding: 8px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #191919;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
`

const NetworkOption = styled.button<{ active?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: ${({ active }) => (active ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`

const ChainIcon = styled.img`
  display: inline-flex;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const HeaderAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-left: auto;
`

const HeaderAddressTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`

const HeaderLinkButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: #bdbdbd;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

const VerifiedWalletIcon = styled(VerifiedIcon)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`

const AddressText = styled(Text)`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* The panel is a scrolling flex column. Without this, a long section — the
     connected view's token list — is compressed to fit instead of scrolling,
     which is a squashed layout rather than a scrollbar. */
  flex-shrink: 0;
`

const IntroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

/*
 * Deliberately compact. This card says which wallet is connected and offers
 * one action; at 110px tall it was taking the height of two balance rows and
 * pushing the tokens — the thing people open this panel for — under the fold
 * on a laptop. Sized to its content so Send and Receive can sit beside the
 * balances without costing anyone a scroll.
 */
const WalletCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 0;
  padding: 12px 16px;
  border-radius: 16px;
  background: #1b1b1b;
  border: 1px solid rgba(255, 255, 255, 0.04);
`

const WalletCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const ConnectorIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;

  img,
  svg {
    width: 38px;
    height: 38px;
    object-fit: contain;
  }
`

const WalletCardAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #1f1f1f;
  cursor: pointer;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray2};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const ProviderButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #1b1b1b;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;

  &:hover {
    background: #202020;
  }
`

const ProviderButtonLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const ProviderArrow = styled.span`
  color: ${({ theme }) => theme.colors.gray2};
  font-size: 22px;
  line-height: 1;
`

const RotatingRefresh = styled(RefreshCw)<{ $spinning?: boolean }>`
  animation: ${({ $spinning }) => ($spinning ? 'walletPanelSpin 1s linear infinite' : 'none')};

  @keyframes walletPanelSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const NETWORKS = [
  { chainId: ETH_CHAIN_ID_NUMBER, label: 'Ethereum', iconSrc: '/images/tokens/eth.png' },
  { chainId: BASE_CHAIN_ID_NUMBER, label: 'Base', iconSrc: '/images/tokens/base.png' }
]

/**
 * Wallet panel rebuilt for standalone MPRO Lab.
 * Keeps the legacy slide-in layout and visual hierarchy, without bringing back metapro-specific flows.
 */
const ConnectVerifyPanel: React.FC<ConnectVerifyPanelProps> = ({
  handleLogin,
  isConnectWalletPanelOpen,
  setIsConnectWalletPanelOpen,
  logout,
  account,
  isActive,
  handlePanelBlur,
  connectors,
  chainId,
  setupNetwork
}) => {
  const { t } = useTranslation()
  const [networkMenuOpen, setNetworkMenuOpen] = useState(false)
  // The panel stays mounted while closed, so opening it must re-read the
  // chain — otherwise it shows whatever was true when the app first loaded.
  const { rows, loading, refetch } = useWalletPanelOnchainBalances(account, isConnectWalletPanelOpen)

  const activeConnectorId =
    typeof window !== 'undefined'
      ? (localStorage.getItem(initialConnectorIdLocalStorageKey) as ConnectorName | null)
      : null

  const activeConnector = connectors.find(({ connectorId }) => connectorId === activeConnectorId) ?? connectors[0]

  const activeNetwork = NETWORKS.find((network) => network.chainId === chainId) ?? NETWORKS[0]

  const onPickProvider = (args: LoginHandlerArgs) => {
    handleLogin(args)
  }

  if (!isConnectWalletPanelOpen) return null

  return (
    <Backdrop
      onClick={(event) => {
        if (event.target === event.currentTarget) handlePanelBlur()
      }}
    >
      <Panel>
        <HeaderRow>
          {isActive && account ? (
            <HeaderMeta>
              <NetworkSelectorWrap>
                <NetworkSelector type="button" onClick={() => setNetworkMenuOpen((prev) => !prev)}>
                  <ChainIcon src={activeNetwork.iconSrc} alt={`${activeNetwork.label} logo`} />
                  <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{activeNetwork.label}</Text>
                  <ChevronDown size={18} />
                </NetworkSelector>
                {networkMenuOpen && (
                  <NetworkMenu>
                    {NETWORKS.map((network) => (
                      <NetworkOption
                        key={network.chainId}
                        active={network.chainId === activeNetwork.chainId}
                        type="button"
                        onClick={() => {
                          setNetworkMenuOpen(false)
                          if (network.chainId === chainId || !setupNetwork) return
                          void Promise.resolve(
                            setupNetwork({ chainId: network.chainId, modal: true, error: false })
                          ).catch(() => undefined)
                        }}
                      >
                        <ChainIcon src={network.iconSrc} alt={`${network.label} logo`} />
                        <Text variant={TextVariant.BODY_DEFAULT}>{network.label}</Text>
                      </NetworkOption>
                    ))}
                  </NetworkMenu>
                )}
              </NetworkSelectorWrap>

              <HeaderAddress>
                <VerifiedWalletIcon />
                <HeaderAddressTextBlock>
                  <AddressText variant={TextVariant.BODY_DEFAULT_BOLD}>{shortenAddress(account, 4, 4)}</AddressText>
                  <HeaderLinkButton
                    type="button"
                    onClick={() => {
                      logout()
                      setNetworkMenuOpen(false)
                    }}
                  >
                    switch wallet
                  </HeaderLinkButton>
                </HeaderAddressTextBlock>
              </HeaderAddress>
            </HeaderMeta>
          ) : (
            <Text variant={TextVariant.BODY_DEFAULT}>{t('walletConnect.connectWallet')}</Text>
          )}

          <IconButton type="button" onClick={() => setIsConnectWalletPanelOpen(false)} aria-label="Close wallet panel">
            <X size={22} />
          </IconButton>
        </HeaderRow>
        {isActive && account ? (
          <Section>
            <Divider />

            <WalletCard>
              <WalletCardLeft>
                <ConnectorIconWrap>{activeConnector?.icon}</ConnectorIconWrap>
                <Text variant={TextVariant.BODY_DEFAULT}>{activeConnector?.title ?? ConnectorNameEnum.METAMASK}</Text>
              </WalletCardLeft>
              <WalletCardAction
                type="button"
                onClick={() => {
                  logout()
                  setNetworkMenuOpen(false)
                }}
                aria-label={t('walletPanel.switchWallet')}
              >
                <LogOut size={18} />
              </WalletCardAction>
            </WalletCard>

            <SectionHeader>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('walletPanel.onchainBalances')}</Text>
              <RefreshButton
                type="button"
                onClick={() => {
                  void refetch()
                }}
                aria-label={t('walletPanel.refreshBalances')}
              >
                <RotatingRefresh size={18} $spinning={loading} />
              </RefreshButton>
            </SectionHeader>

            {account && (
              <WalletSendReceive account={account} rows={rows} walletChainId={chainId} onSent={() => void refetch()} />
            )}

            <WalletPanelTokenRows rows={rows} onRetry={() => void refetch()} />
          </Section>
        ) : (
          <Section>
            <IntroCopy>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('connectWalletView.selectProvider')}</Text>
              <Text variant={TextVariant.BODY_SMALL}>
                Choose the wallet you want to use with the standalone MPRO Lab app.
              </Text>
            </IntroCopy>

            <ProviderList>
              {connectors.map(({ title, icon, connectorId }) => (
                <ProviderButton
                  key={title}
                  type="button"
                  onClick={() =>
                    onPickProvider({
                      connectorId,
                      title: title as ConnectorNameEnum | string
                    })
                  }
                >
                  <ProviderButtonLeft>
                    <ConnectorIconWrap>{icon as React.ReactElement}</ConnectorIconWrap>
                    <Text variant={TextVariant.BODY_DEFAULT}>{title}</Text>
                  </ProviderButtonLeft>
                  <ProviderArrow>&rsaquo;</ProviderArrow>
                </ProviderButton>
              ))}
            </ProviderList>
          </Section>
        )}

        <TermsNote>
          By connecting a wallet you accept the{' '}
          <a href="/terms-and-conditions" target="_blank" rel="noreferrer noopener">
            Terms of Use
          </a>
          . If you do not, do not connect.
        </TermsNote>
      </Panel>
    </Backdrop>
  )
}

export default ConnectVerifyPanel

export type { Login, LoginHandlerArgs, WagmiConnector }
